import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { isAuthenticated } from '@/utils/auth';
import { removeToken } from '@/utils/tokenManager';
import { gql, useMutation } from '@apollo/client';
import {
   Bell,
   Calendar,
   ChevronLeft,
   ClipboardList,
   FileText,
   HelpCircle,
   Home,
   LogOut,
   User
} from "lucide-react";
import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
// import permitTreeLogo from '@/assets/denr-logo.png';
import {
   Sheet,
   SheetContent,
   SheetDescription,
   SheetHeader,
   SheetTitle,
} from "@/components/ui/sheet";
import { useTour } from '@/contexts/TourContext';
import { usePendingInspections } from '@/pages/user/hooks/usePendingInspections';
import { useUserNotifications } from '@/pages/user/hooks/useUserNotifications';

const LOGOUT_MUTATION = gql`
   mutation Logout {
      logout
   }
`;

const UserSidebar = React.memo(({ isOpen, onToggle }) => {
   const navigate = useNavigate();
   const location = useLocation();
   const [logout] = useMutation(LOGOUT_MUTATION);
   const [isAuth, setIsAuth] = useState(isAuthenticated());
   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
   const { unreadCount } = useUserNotifications();
   const { pendingCount } = usePendingInspections();
   const { startTour } = useTour();

   useEffect(() => {
      const handleResize = () => {
         setIsMobile(window.innerWidth < 1024);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   useEffect(() => {
      if (isOpen) {
         onToggle(false);
      }
   }, []);

   const handleLogout = useCallback(async () => {
      try {
         await logout();
         removeToken();
         localStorage.removeItem('user');
         setIsAuth(false);
         if (isOpen) onToggle();
         navigate('/auth');
      } catch (error) {
         console.error('Logout failed:', error);
      }
   }, [logout, navigate, isOpen, onToggle]);

   useEffect(() => {
      const authStatus = isAuthenticated();
      setIsAuth(authStatus);
      if (!authStatus) {
         if (isOpen) onToggle();
         navigate('/auth');
      }
   }, [navigate, isOpen, onToggle]);

   const sidebarTourSteps = [
      {
         target: '.home-icon',
         content: 'Return to your dashboard to see an overview of your applications and notifications',
         placement: 'right',
      },
      {
         target: '.apply-icon',
         content: 'Start a new permit application - choose from various permit types based on your needs',
         placement: 'right',
      },
      {
         target: '.status-icon',
         content: 'Track all your applications - view their current status and take necessary actions',
         placement: 'right',
      },
      {
         target: '.inspections-icon',
         content: 'View and manage scheduled inspections for your applications',
         placement: 'right',
      },
      {
         target: '.notifications-icon',
         content: 'Stay updated with important notifications about your applications and permits',
         placement: 'right',
      }
   ];

   const mainNavItems = [
      { to: "/home", icon: Home, text: "Home" },
      { to: "/permits", icon: FileText, text: "Apply" },
      { to: "/applicationsStatus", icon: ClipboardList, text: "Application Status" },
      {
         to: "/inspections",
         icon: Calendar,
         text: "My Inspections",
         badge: pendingCount > 0 ? pendingCount : null
      },
      {
         to: "/notifications",
         icon: Bell,
         text: "Notifications",
         badge: unreadCount > 0 ? unreadCount : null
      },
      {
         to: "#",
         icon: HelpCircle,
         text: "Help",
         onClick: (e) => {
            e.preventDefault();
            startTour(sidebarTourSteps, 'sidebar-tour');
         }
      }
   ];

   const accountNavItems = [
      { to: "/profile", icon: User, text: "Profile" },
      { to: "/auth", icon: LogOut, text: "Logout", onClick: handleLogout }
   ];

   const SidebarItem = ({ item, isCollapsed }) => {
      const isActive = location.pathname === item.to;

      const handleClick = (e) => {
         if (item.onClick) {
            item.onClick(e);
         } else if (isMobile) {
            onToggle(false);
         }
      };

      const iconClass = `${item.text.toLowerCase()}-icon`;

      return (
         <NavLink
            to={item.to}
            onClick={handleClick}
            className={cn(
               "relative flex h-10 w-full items-center",
               "hover:bg-accent rounded-lg transition-colors",
               "px-3"
            )}
         >
            <div className="flex items-center w-full">
               <div className={`w-[24px] flex items-center justify-center relative ${iconClass}`}>
                  <item.icon className={cn(
                     "h-4 w-4",
                     isActive ? "text-accent-foreground" : "text-muted-foreground"
                  )} />
                  {item.badge && (
                     <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs
                        font-medium px-1.5 py-0.5 rounded-full min-w-[12px] h-[12px]
                        flex items-center justify-center">
                        {item.badge > 99 ? '99+' : item.badge}
                     </span>
                  )}
               </div>
               <span className={cn(
                  "ml-2 text-[15px] absolute left-[48px]",
                  "transition-all duration-300",
                  isCollapsed ? "opacity-0 -translate-x-2" : "opacity-100 translate-x-0",
                  isActive ? "text-accent-foreground" : "text-muted-foreground"
               )}>
                  {item.text}
               </span>
            </div>
         </NavLink>
      );
   };

   const SidebarContent = ({ isCollapsed = false }) => (
      <div className="flex h-full flex-col">
         <div className={cn(
            "flex h-[12px] items-center",
            isCollapsed ? "justify-center" : "px-4"
         )}>
            <NavLink to="/home" className="flex items-center gap-2"> </NavLink>
         </div>
         <ScrollArea className="flex-1">
            <div className="space-y-1 px-2 pt-1 font-medium">
               {mainNavItems.map((item, i) => (
                  <SidebarItem key={i} item={item} isCollapsed={isCollapsed} />
               ))}
            </div>
         </ScrollArea>
         <div className={cn(
            "mt-auto border-t px-2 py-2"
         )}>
            {accountNavItems.map((item, i) => (
               <SidebarItem key={i} item={item} isCollapsed={isCollapsed} />
            ))}
         </div>
         <Button
            variant="ghost"
            className={cn(
               "h-10 w-full border-t",
               isCollapsed ? "px-2" : "px-4"
            )}
            onClick={onToggle}
         >
            <ChevronLeft className={cn(
               "h-4 w-4 transition-all",
               isOpen ? "rotate-0" : "rotate-180"
            )} />
         </Button>
      </div>
   );

   if (isMobile) {
      return (
         <Sheet open={isOpen} onOpenChange={onToggle}>
            <SheetContent side="left" className="p-0 w-[270px] flex flex-col">
               <SheetHeader className="h-16 border-b">
                  <div className="flex items-center h-full px-6">
                     <SheetTitle className="text-left">Menu</SheetTitle>
                     <SheetDescription className="sr-only">
                        Navigation menu for user dashboard
                     </SheetDescription>
                  </div>
               </SheetHeader>
               <div className="flex-1 overflow-auto">
                  <SidebarContent />
               </div>
            </SheetContent>
         </Sheet>
      );
   }

   return (
      <div className={cn(
         "fixed top-16 left-0 z-30 h-[calc(100%-4rem)] border-r bg-background",
         isOpen ? "w-[270px]" : "w-[64px]",
         "transition-[width] duration-300 ease-in-out"
      )}>
         <SidebarContent isCollapsed={!isOpen} />
      </div>
   );
});

export default UserSidebar;
